import React from 'react';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  SxProps,
} from '@mui/material';
import SearchDropDown from '../../../common/SearchDropDown';
import ErrorBadge from '../../../common/ErrorBadge';
import { NavigationGroup } from '../types';
import { PropertyField } from '../../../../system/field';
import SetFieldDefaults from '../SetFieldDefaults';
import { FeatureFlags } from '../../../../flags';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';

interface DesktopNavigationProps {
  groups: NavigationGroup[];
  activeGroup: string;
  activeSection: string;
  headerHeight: number;
  sectionErrors: { [key: string]: number };
  groupErrors: { [key: string]: number };
  setDefaultsStyles: SxProps;
  handleSearchEntriesChange: (fields: PropertyField[] | null) => void;
  handleGroupClick: (
    groupName: string,
    fieldName?: string,
    isMobile?: boolean,
    followError?: boolean,
  ) => void;
  handleSectionClick: (sectionName: string, followError?: boolean) => void;
}

const DesktopNavigation: React.FC<DesktopNavigationProps> = ({
  groups,
  activeGroup,
  activeSection,
  headerHeight,
  sectionErrors,
  groupErrors,
  setDefaultsStyles,
  handleGroupClick,
  handleSectionClick,
  handleSearchEntriesChange,
}) => {
  const { hasFeature } = useFeatureFlags();

  const hasSetFieldDefaults = hasFeature(FeatureFlags.NGW_EDIT_MEMBER_DEFAULTS);

  return (
    <Box
      data-testid="edit-navigation"
      sx={{
        width: 400,
        bgcolor: 'grey.lightest',
        position: 'sticky',
        top: headerHeight,
        alignSelf: 'flex-start',
        borderRight: '1px solid',
        borderColor: 'grey.300',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: `calc(100vh - ${headerHeight}px)`,
      }}
    >
      <Typography
        sx={{
          padding: '12px 20px',
          color: 'grey.700',
          fontSize: '20px',
          fontWeight: '600',
          marginTop: '10px',
        }}
      >
        Listing Sections
      </Typography>
      <Box sx={{ mb: 2, px: 2, pt: 0 }}>
        <SearchDropDown
          data={groups}
          onResultsChange={handleSearchEntriesChange}
          onClick={(groupName, fieldLabel) => {
            handleGroupClick(groupName, fieldLabel, false, false);
          }}
        />
      </Box>
      <Divider />
      <Box
        sx={{ flexGrow: 1, overflowY: 'auto' }}
        data-testid="body-content"
        data-nav-sections
      >
        <List data-testid="desktop-nav">
          {groups.map((group) => (
            <React.Fragment key={group.name}>
              <ListItem disablePadding>
                <ListItemButton
                  data-group-id={group.name}
                  selected={activeGroup === group.name}
                  onClick={() =>
                    handleGroupClick(group.name, undefined, false, false)
                  }
                  sx={{
                    marginTop: '1px',
                    lineHeight: '24px',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={group.name}
                    primaryTypographyProps={{
                      fontWeight: '700',
                      fontSize: '14px',
                      color: 'grey.700',
                    }}
                  />
                  {activeGroup !== group.name &&
                    groupErrors[group.name] > 0 && (
                      <ErrorBadge
                        count={groupErrors[group.name]}
                        onClick={() =>
                          handleGroupClick(group.name, undefined, false, true)
                        }
                      />
                    )}
                </ListItemButton>
              </ListItem>
              {activeGroup === group.name && (
                <Box sx={{ marginTop: '8px' }}>
                  {group.sections.map((section) => (
                    <Box
                      key={section.name}
                      data-section-id={section.name}
                      onClick={() => handleSectionClick(section.name, false)}
                      sx={{
                        borderLeft:
                          activeSection === section.name
                            ? '6px solid'
                            : '6px solid transparent',
                        borderLeftColor:
                          activeSection === section.name
                            ? 'secondary.dark'
                            : 'transparent',
                        backgroundColor:
                          activeSection === section.name
                            ? 'action.hover'
                            : 'transparent',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 20px',
                        cursor: 'pointer',
                        scrollMargin: '20px',
                      }}
                    >
                      <Typography
                        sx={{
                          color: 'grey.700',
                          fontSize: '14px',
                          fontWeight: '400',
                          flexGrow: 1,
                          textAlign: 'left',
                        }}
                      >
                        {section.name}
                      </Typography>
                      {sectionErrors[section.name] > 0 && (
                        <ErrorBadge
                          count={sectionErrors[section.name]}
                          onClick={() => handleSectionClick(section.name, true)}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>
      {hasSetFieldDefaults && (
        <SetFieldDefaults
          outlinedIcon={true}
          content={
            <Typography fontWeight="bold">Set Field Defaults</Typography>
          }
          styles={setDefaultsStyles}
        />
      )}
    </Box>
  );
};

export default DesktopNavigation;
